import React, { useCallback, useEffect, useRef, useState } from "react";
import { BsArrowDown } from "react-icons/bs";
import PropTypes from "prop-types";
import { Button, ButtonGroup, Col, Form, Row } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { FaSearch } from "react-icons/fa";
import { RiFilterLine, RiFilterOffLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import useApiClient from "../../../hooks/apiClient";
import { paginatedViewActions } from "../../../redux/paginatedView";
import ButtonPermission from "../Button/ButtonPermission";
import TextSpinner from "../Spinner/TextSpinner";
import useDeleteItemOcorrencia from "./hooks/useDeleteItemOcorrencia";
import constants from "../../../pages/Constants";

/**
 * Componente genérico para exbição de views paginadas
 */
const OcorrenciaPaginatedView = ({
  columns,
  title,
  keyField,
  descriptionField,
  defaultSearch,
  defaultSearchPlaceholder,
  queryService,
  deleteService,
  permissionInsert,
  permissionDelete,
  history,
  createRoute,
  editRoute,
  expandComponent,
  advancedSearchFormik,
  selectableRows,
  onSelectedRowsChange,
  contextMessage,
  contextActions,
  titleBar,
  titleBarParams,
  onRowClicked,
  footer,
  children,
  reduxKey,
  stateKey,
  pageSizeRows = 10,
}) => {
  const [rows, setRows] = useState([]);
  const [pageSize, setPageSize] = useState(pageSizeRows);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRows, setTotalRows] = useState(1);
  const [inputSearchArgument, setInputSearchArgument] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const paginatedViewState = useSelector((state) => state.paginatedView);
  const { advancedSearchParams, showAdvancedSearch } = paginatedViewState[reduxKey] || {};
  const [searchParams, setSearchParams] = useState(showAdvancedSearch ? advancedSearchParams : {});
  const dispatch = useDispatch();

  const { request, data, errors, isLoading } = useApiClient();

  useEffect(() => {
    setRows([]);
    const params = { ...searchParams, ...titleBarParams };
    request(queryService, {
      ...params,
      pageSize,
      pageNumber,
    });
  }, [request, queryService, showAdvancedSearch, searchParams, titleBarParams, pageSize, pageNumber]);

  const handlePageChange = useCallback((newPageNumber) => {
    setPageNumber(newPageNumber);
  }, []);

  const handlePerRowsChange = useCallback((newPageSize) => {
    setPageSize(newPageSize);
    setPageNumber(1);
  }, []);

  const refreshItems = useCallback(() => {
    window.location.reload(false);
  }, []);

  const { excluirConfirmModal } = useDeleteItemOcorrencia({
    columns,
    deleteService,
    keyField,
    descriptionField,
    permissionDelete,
    onDeleteItem: refreshItems,
  });

  useEffect(() => {
    if (data?.result) {
      if (data.result.length && !data.result[0].totalRows) {
        console.error(`[DEV] A propriedade "totalRows" é obrigatória nos resultados paginados. `);
        return;
      }
      setRows(data.result || []);
      setTotalRows(data.result.length ? data.result[0].totalRows : 0);
      setSelectedRows([]);
      setToggleCleared((curr) => !curr);
    }

    if (errors) {
      toast.error(errors.message);
    }
  }, [data, errors]);

  const viewStateKey = useRef(null);
  const { setValues } = advancedSearchFormik ?? {};
  useEffect(() => {
    if (viewStateKey.current === null) {
      viewStateKey.current = stateKey ?? "none";
    } else if (stateKey && stateKey !== viewStateKey.current) {
      viewStateKey.current = stateKey;
      setPageNumber(1);
      setSearchParams((curr) => ({ ...curr }));
    }
  }, [stateKey]);

  // INIT: Carregar estado do form de busca avançada
  useEffect(() => {
    if (showAdvancedSearch) {
      setValues((curr) => ({ ...curr, ...advancedSearchParams }));
    }
  }, [setValues, showAdvancedSearch, advancedSearchParams]);

  if (expandComponent && rows.length) {
    rows.forEach((row) => {
      const expandableRow = row;
      expandableRow.setRows = setRows;
    });
  }

  return (
    <>
      <Row hidden={!title}>
        <Col sm="12">
          <h1 className="h3 mb-4 text-gray-800">{title}</h1>
        </Col>
      </Row>
      {excluirConfirmModal}
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            {defaultSearch && (
              <div className="card-header pt-3 pb-0 flex-row w-100">
                <Form.Group as={Row} className="d-flex flex-row">
                  <h6 className="font-weight-bold text-primary flex-grow-1 ml-2 mt-2">
                    {title ? `Consulta de ${title}` : ""}
                  </h6>
                  {titleBar ??
                    (permissionInsert && (
                      <>
                        <ButtonPermission
                          permission={permissionInsert}
                          className="btn btn-primary btn-md btn-user"
                          onClick={() => history.push(createRoute || editRoute)}
                          name="Novo"
                        />
                        <div className="card-divider d-none d-sm-block" />
                      </>
                    ))}
                  {!showAdvancedSearch && (
                    <ButtonGroup>
                      <Form.Control
                        className="w-auto border-top-right-0 border-bottom-right-0"
                        type="text"
                        placeholder={defaultSearchPlaceholder}
                        value={inputSearchArgument}
                        onChange={(e) => setInputSearchArgument(e.target.value)}
                      />
                      <Button
                        className="btn-search-inside btn-md mr-3"
                        onClick={() => {
                          setPageNumber(1);
                          setSearchParams({ searchArgument: inputSearchArgument });
                        }}
                      >
                        <FaSearch />
                      </Button>
                    </ButtonGroup>
                  )}
                  {advancedSearchFormik && (
                    <Button
                      className="btn btn-primary btn-md mr-2"
                      onClick={() => {
                        dispatch(
                          paginatedViewActions.setShowAdvancedSearch({
                            reduxKey,
                            showAdvancedSearch: !showAdvancedSearch,
                          }),
                        );
                      }}
                    >
                      {showAdvancedSearch ? <RiFilterOffLine /> : <RiFilterLine />}
                    </Button>
                  )}
                </Form.Group>
                {showAdvancedSearch && (
                  <>
                    <hr />
                    {children}
                    <Form.Group as={Row} className="d-flex flex-row">
                      <Col sm="12">
                        <Button
                          className="btn btn-primary btn-md mr-2"
                          onClick={() => {
                            setPageNumber(1);
                            setSearchParams({ ...advancedSearchFormik.values });
                            dispatch(
                              paginatedViewActions.saveParams({ reduxKey, params: { ...advancedSearchFormik.values } }),
                            );
                          }}
                        >
                          Buscar
                        </Button>
                        <Button
                          className="btn btn-primary btn-md mr-2"
                          variant="light"
                          onClick={() => {
                            setPageNumber(1);
                            advancedSearchFormik.resetForm();
                          }}
                        >
                          Limpar
                        </Button>
                      </Col>
                    </Form.Group>
                  </>
                )}
              </div>
            )}

            <div className="card-body">
              <DataTable
                noHeader={!selectedRows.length}
                keyField={keyField}
                columns={columns}
                data={rows}
                progressPending={isLoading}
                noDataComponent="Nenhum registro para exibir"
                progressComponent={<TextSpinner />}
                selectableRows={selectableRows || false}
                onSelectedRowsChange={(selection) => {
                  if (typeof onSelectedRowsChange === "function") {
                    onSelectedRowsChange(selection);
                  }
                  setSelectedRows(selection.selectedRows);
                }}
                contextMessage={contextMessage}
                contextActions={contextActions}
                clearSelectedRows={toggleCleared}
                pointerOnHover
                onRowClicked={(item) => {
                  if (typeof onRowClicked === "function") {
                    onRowClicked(item);
                  } else if (editRoute) {
                    history.push({
                      pathname: editRoute,
                      state: { itemKey: item[keyField] },
                    });
                  }
                }}
                sortable
                sortIcon={<BsArrowDown />}
                onChangeRowsPerPage={handlePerRowsChange}
                onChangePage={handlePageChange}
                pagination
                paginationComponentOptions={constants.paginationComponentOptions}
                paginationTotalRows={totalRows}
                paginationPerPage={pageSize}
                expandableRows={!!expandComponent}
                expandableRowsComponent={expandComponent}
              />
              {footer}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

OcorrenciaPaginatedView.propTypes = {
  /** Título da página */
  title: PropTypes.string,
  /** Nome da propriedade que identifica unicamente cada item da tabela */
  keyField: PropTypes.string.isRequired,
  /** Campo utilizado como referência para descrever cada item em diálogos */
  descriptionField: PropTypes.string.isRequired,
  /** Array contendo a descrição das colunas. Mais informações: https://github.com/jbetancur/react-data-table-component  */
  columns: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })).isRequired,
  /** Chave que identifica o estado do componente no Redux. **Necessária** caso utilize a busca avançada. */
  reduxKey: PropTypes.string,
};

OcorrenciaPaginatedView.defaultProps = { reduxKey: "none", title: "" };

export default OcorrenciaPaginatedView;
